import { useEffect, useMemo, useState } from "react"
import { useConfig } from "@/config/context"
import { parseProfileEvent } from "@/nostr/api/profiles"
import { relayInfoService } from "@/nostr/relayInfo"
import { KINDS, type NostrEvent, type Profile, type RelayInfo } from "@/nostr/types"
import { useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { useSettings } from "@/state/SettingsContext"
import { uniqueRelayUrls } from "@/utils/url"

export type SearchTab = "notes" | "profiles" | "relays"

export interface SearchResults {
  notes: NostrEvent[]
  profiles: Profile[]
  relays: RelayInfo[]
  /** `relay` = NIP-50 relay search, `local` = client side filter, `idle` = empty query. */
  mode: "relay" | "local" | "idle"
  loading: boolean
  searchedRelays: string[]
}

const IDLE: SearchResults = {
  notes: [],
  profiles: [],
  relays: [],
  mode: "idle",
  loading: false,
  searchedRelays: [],
}

/**
 * Search with a clear contract:
 *
 * - notes and profiles use NIP-50 `search` filters on the configured search
 *   relays; when no search relay is configured the query is applied locally to
 *   recent events, and the UI says so.
 * - relays are matched against the relay index (the user's relays, the
 *   configured discovery relays and the NIP-11 metadata cache).
 */
export function useSearch(query: string, tab: SearchTab): SearchResults {
  const config = useConfig()
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const { settings } = useSettings()
  const [term, setTerm] = useState("")
  const [results, setResults] = useState<SearchResults>(IDLE)

  const searchRelays = uniqueRelayUrls(
    settings.searchRelays.length > 0 ? settings.searchRelays : config.relays.searchRelays,
  )
  const searchRelaysKey = searchRelays.join("|")
  const relayIndex = useMemo(
    () => uniqueRelayUrls([...appRelays, ...config.relays.discoveryRelays]),
    [appRelays, config.relays.discoveryRelays],
  )
  const relayIndexKey = relayIndex.join("|")

  useEffect(() => {
    const timer = setTimeout(() => setTerm(query.trim()), 350)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!term) {
      setResults(IDLE)
      return
    }

    let cancelled = false
    setResults((current) => ({ ...current, loading: true, mode: searchRelays.length > 0 ? "relay" : "local" }))

    if (tab === "relays") {
      const lowered = term.toLowerCase()
      const matchesByUrl = relayIndex.filter((url) => url.toLowerCase().includes(lowered)).slice(0, 40)
      setResults({
        notes: [],
        profiles: [],
        relays: matchesByUrl.map((url) => ({
          url,
          shortUrl: url.replace(/^wss?:\/\//, "").replace(/\/$/, ""),
          supported_nips: [],
        })),
        mode: "local",
        loading: true,
        searchedRelays: relayIndex,
      })

      // Enrich from NIP-11 metadata (name / description matching).
      const candidates = relayIndex.slice(0, 40)
      Promise.all(candidates.map((url) => relayInfoService.get(url)))
        .then((infos) => {
          if (cancelled) return
          const matched = infos.filter((info) => {
            const haystack = `${info.url} ${info.name ?? ""} ${info.description ?? ""}`.toLowerCase()
            return haystack.includes(lowered)
          })
          setResults({
            notes: [],
            profiles: [],
            relays: matched.slice(0, 60),
            mode: "local",
            loading: false,
            searchedRelays: relayIndex,
          })
        })
        .catch(() => {
          if (!cancelled) setResults((current) => ({ ...current, loading: false }))
        })

      return () => {
        cancelled = true
      }
    }

    const kinds = tab === "profiles" ? [KINDS.PROFILE] : [KINDS.NOTE, KINDS.ARTICLE]
    const collected = new Map<string, NostrEvent>()

    const finish = () => {
      if (cancelled) return
      const events = [...collected.values()].sort((a, b) => b.created_at - a.created_at)
      if (tab === "profiles") {
        const profiles = events
          .map((event) => parseProfileEvent(event))
          .filter((profile): profile is Profile => Boolean(profile))
        setResults({
          notes: [],
          profiles,
          relays: [],
          mode: searchRelays.length > 0 ? "relay" : "local",
          loading: false,
          searchedRelays: searchRelays.length > 0 ? searchRelays : appRelays,
        })
        return
      }
      setResults({
        notes: events,
        profiles: [],
        relays: [],
        mode: searchRelays.length > 0 ? "relay" : "local",
        loading: false,
        searchedRelays: searchRelays.length > 0 ? searchRelays : appRelays,
      })
    }

    if (searchRelays.length > 0) {
      const subscription = pool.subscribe({ kinds, search: term, limit: 60 }, {
        relays: searchRelays,
        onEvent: (event) => {
          store.add(event)
          collected.set(event.id, event)
        },
        onEose: finish,
      })
      return () => {
        cancelled = true
        subscription.close()
      }
    }

    // Local fallback: fetch a recent page from the user's relays and filter it.
    const subscription = pool.subscribe({ kinds, limit: 400 }, {
      relays: appRelays,
      onEvent: (event) => {
        store.add(event)
        const haystack =
          tab === "profiles"
            ? event.content
            : `${event.content} ${event.tags.filter((tag) => tag[0] === "t").map((tag) => tag[1]).join(" ")}`
        if (haystack.toLowerCase().includes(term.toLowerCase())) collected.set(event.id, event)
      },
      onEose: finish,
    })
    return () => {
      cancelled = true
      subscription.close()
    }
  }, [term, tab, searchRelaysKey, relayIndexKey, pool, store, appRelays])

  return results
}
